import { ThreadAIOutcome } from '@weave/schema-gen-ts/dist/schemas/sms/shared/v2/enums.pb';
import { TFunction } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import {
  VoiceAiActivity,
  VoiceAiActivityKey,
  VoiceAiActivityMappedInfo,
  ResolvedVoiceAiActivitiesResult,
} from './types';

export const VOICE_AI_ACTIVITY_PRIORITY: VoiceAiActivityKey[] = [
  'transfer_missed',
  'unable_to_answer',
  'call_disrupted',
  'call_credits_exhausted',
  'added_to_schedule',
  'appointment_requested',
  'rescheduled',
  'cancelled',
  'confirmed',
  'incomplete_call',
  'completed_call',
  'continued_over_text',
  'transferred_to_staff',
  'unknown',
];

const OUTCOME_TO_ACTIVITY_KEY: Partial<Record<ThreadAIOutcome, VoiceAiActivityKey>> = {
  [ThreadAIOutcome.THREAD_AI_OUTCOME_VOICE_ROUTE_MISSED]: 'transfer_missed',
  [ThreadAIOutcome.THREAD_AI_OUTCOME_VOICE_FATAL]: 'unable_to_answer',
  [ThreadAIOutcome.THREAD_AI_OUTCOME_VOICE_CALL_CREDITS_OVER]: 'call_credits_exhausted',
  [ThreadAIOutcome.THREAD_AI_OUTCOME_SCHEDULING_COMPLETE]: 'added_to_schedule',
  [ThreadAIOutcome.THREAD_AI_OUTCOME_VOICE_APPOINTMENT_SCHEDULE]: 'added_to_schedule',
  [ThreadAIOutcome.THREAD_AI_OUTCOME_VOICE_APPOINTMENT_REQUEST]: 'appointment_requested',
  [ThreadAIOutcome.THREAD_AI_OUTCOME_RESCHEDULING_REQUEST]: 'rescheduled',
  [ThreadAIOutcome.THREAD_AI_OUTCOME_RESCHEDULING_COMPLETION]: 'rescheduled',
  [ThreadAIOutcome.THREAD_AI_OUTCOME_VOICE_APPOINTMENT_RESCHEDULE]: 'rescheduled',
  [ThreadAIOutcome.THREAD_AI_OUTCOME_CANCELLATION_REQUEST]: 'cancelled',
  [ThreadAIOutcome.THREAD_AI_OUTCOME_CANCELLATION_COMPLETION]: 'cancelled',
  [ThreadAIOutcome.THREAD_AI_OUTCOME_VOICE_APPOINTMENT_CANCEL]: 'cancelled',
  [ThreadAIOutcome.THREAD_AI_OUTCOME_APPOINTMENT_CONFIRMATION]: 'confirmed',
  [ThreadAIOutcome.THREAD_AI_OUTCOME_VOICE_APPOINTMENT_CONFIRM]: 'confirmed',
  [ThreadAIOutcome.THREAD_AI_OUTCOME_VOICE_INCOMPLETE]: 'incomplete_call',
  [ThreadAIOutcome.THREAD_AI_OUTCOME_VOICE_END_CALL]: 'completed_call',
  [ThreadAIOutcome.THREAD_AI_OUTCOME_VOICE_TEXT_HANDOFF]: 'continued_over_text',
  [ThreadAIOutcome.THREAD_AI_OUTCOME_VOICE_ROUTE_TRANSFERRED]: 'transferred_to_staff',
};

export const getVoiceAiActivityKey = (outcome?: ThreadAIOutcome): VoiceAiActivityKey => {
  if (!outcome) return 'unknown';

  return OUTCOME_TO_ACTIVITY_KEY[outcome] ?? 'unknown';
};

export const buildVoiceAiActivityMap = (
  t: TFunction<'thread-body', undefined>
): Record<VoiceAiActivityKey, VoiceAiActivityMappedInfo> => ({
  transfer_missed: {
    label: t('Transfer Missed'),
    iconName: 'aineeds-attention',
    iconColor: theme.colors.warning80,
    iconGradientBg: 'linear-gradient(98.12deg, #FFE895 5.08%, #FFF6D6 99.72%)',
  },
  unable_to_answer: {
    label: t('Unable to Answer'),
    iconName: 'phone-missed',
    iconColor: theme.colors.critical60,
    iconGradientBg: 'linear-gradient(98.12deg, #FFB8B3 5.08%, #FFF1F0 99.72%)',
  },
  call_disrupted: {
    label: t('Call Disrupted'),
    iconName: 'phone-hangup-small',
    iconColor: theme.colors.critical60,
    iconGradientBg: 'linear-gradient(98.12deg, #FFB8B3 5.08%, #FFF1F0 99.72%)',
  },
  call_credits_exhausted: {
    label: t('Call Credits Exhausted'),
    iconName: 'alert-small',
    iconColor: theme.colors.warning80,
  },
  added_to_schedule: {
    label: t('Added to Schedule'),
    iconName: 'calendar-scheduled',
    iconColor: theme.colorsV2.blue50(),
  },
  appointment_requested: {
    label: t('Appointment Requested'),
    iconName: 'calendar-scheduled',
    iconColor: theme.colorsV2.blue50(),
  },
  rescheduled: {
    label: t('Rescheduled'),
    iconName: 'calendar-rescheduled',
    iconColor: theme.colorsV2.eggplant50(),
  },
  cancelled: {
    label: t('Cancelled'),
    iconName: 'calendar-canceled',
    iconColor: theme.colors.critical50,
  },
  confirmed: {
    label: t('Confirmed'),
    iconName: 'calendar-confirmed',
    iconColor: theme.colorsV2.blue50(),
  },
  incomplete_call: {
    label: t('Incomplete Call'),
    iconName: 'help',
    iconColor: theme.colors.neutral90,
    iconGradientBg: '#0000001A',
  },
  completed_call: {
    label: t('Completed Call'),
    iconName: 'check',
    iconColor: theme.colors.success80,
  },
  continued_over_text: {
    label: t('Continued Over Text'),
    iconName: 'message',
    iconColor: theme.colors.neutral70,
  },
  transferred_to_staff: {
    label: t('Transferred to Staff'),
    iconName: 'call-forward',
    iconColor: theme.colors.neutral70,
  },
  unknown: {
    label: t('AI Receptionist Activity'),
    iconName: 'aiinfo-small',
    iconColor: theme.colors.neutral70,
  },
});

export const resolveAndSortActivities = (
  activities: Array<VoiceAiActivity | null | undefined> | null | undefined,
  t: TFunction<'thread-body', undefined>
): ResolvedVoiceAiActivitiesResult => {
  const activityMap = buildVoiceAiActivityMap(t);
  const normalizedActivities = (activities || []).filter((activity): activity is VoiceAiActivity => !!activity);

  const resolvedActivities = [...normalizedActivities].map((activity, index) => {
    const key = getVoiceAiActivityKey(activity.outcome);
    const mapped = activityMap[key] || activityMap.unknown;

    return {
      index,
      createdAt: activity.createdAt,
      key,
      label: mapped.label,
      iconName: mapped.iconName,
      iconColor: mapped.iconColor,
      customIconColor: mapped.customIconColor,
      iconGradientBg: mapped.iconGradientBg,
    };
  });

  const getPriorityIndex = (activityKey: VoiceAiActivityKey) => {
    const priority = VOICE_AI_ACTIVITY_PRIORITY.indexOf(activityKey);
    return priority === -1 ? VOICE_AI_ACTIVITY_PRIORITY.length : priority;
  };

  const topActivity =
    [...resolvedActivities].sort((a, b) => {
      const aPriority = getPriorityIndex(a.key);
      const bPriority = getPriorityIndex(b.key);
      if (aPriority !== bPriority) {
        return aPriority - bPriority;
      }
      return a.index - b.index;
    })[0] ?? null;

  const chronologicalActivities = [...resolvedActivities].sort((a, b) => {
    const aTime = a.createdAt ? Date.parse(a.createdAt) : Number.NaN;
    const bTime = b.createdAt ? Date.parse(b.createdAt) : Number.NaN;
    const hasATime = Number.isFinite(aTime);
    const hasBTime = Number.isFinite(bTime);

    if (hasATime && hasBTime && aTime !== bTime) {
      return aTime - bTime;
    }

    if (hasATime !== hasBTime) {
      return hasATime ? -1 : 1;
    }

    return a.index - b.index;
  });

  return {
    topActivity,
    chronologicalActivities,
  };
};
